/* GRN bulk import · Curebay invoice format.
 *
 * Deliberately separate from the "Custom format" parser in Grn.jsx: this reads a
 * Curebay-issued supplier invoice export, whose columns, discount semantics and
 * date formats are its own thing. Keeping the two apart means a change to one
 * layout can never quietly alter how the other is read.
 *
 * Column contract (header row, matched case/space-insensitively):
 *   Invoice Number, Invoice Date, Product Code, Product Name, Packaging,
 *   Batch Number, Expiry Date, Quantity, Scheme Discount, Product Discount,
 *   PTR, MRP, Manufacturer Name, Tax, TaxableValue, HSN Code, Medicine Id,
 *   NetValue, Cash Discount, Marketing Discount, CBDrugCode
 *
 * Verified against real exports:
 *   TaxableValue = (PTR x Quantity - "Scheme Discount") x (1 - "Product Discount" %)
 *   NetValue     = TaxableValue x (1 + Tax %)
 *   "Scheme Discount" is a flat rupee amount off the line gross, and it comes
 *   off BEFORE the percentage trade discount — the two cascade. Verified on
 *   invoice 26CXIR12790849: ONDEM INJECTION, 38 x 9.70 = 368.60 gross, scheme
 *   184.30, then 5% => 175.08, exactly the file's TaxableValue. Reading the
 *   scheme as an additional percentage, or ignoring it, doubles the taxable
 *   value on every schemed line.
 *   Cash / Marketing discounts do NOT appear in TaxableValue — they are
 *   settlement discounts, not line discounts, so they are not mapped onto the
 *   line. Applying them here would understate the taxable value and break the
 *   invoice reconciliation that gates posting.
 */
import * as XLSX from "xlsx";

export const CUREBAY_COLUMNS = [
  "Invoice Number",
  "Invoice Date",
  "Product Code",
  "Product Name",
  "Packaging",
  "Batch Number",
  "Expiry Date",
  "Quantity",
  "Scheme Discount",
  "Product Discount",
  "PTR",
  "MRP",
  "Manufacturer Name",
  "Tax",
  "TaxableValue",
  "HSN Code",
  "Medicine Id",
  "NetValue",
  "Cash Discount",
  "Marketing Discount",
  "CBDrugCode",
];

/* Read a cell by header name, tolerating case / stray whitespace. */
const col = (row, ...names) => {
  for (const n of names) {
    const hit = Object.keys(row).find(
      (k) => String(k).trim().toLowerCase() === String(n).trim().toLowerCase()
    );
    if (hit !== undefined && row[hit] !== "" && row[hit] != null) return row[hit];
  }
  return "";
};

const numOf = (v) => {
  const n = Number(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;


/* Excel serial / Date -> {d, m, y}. Sheets formatted as real dates hand back a
   serial number rather than the text the cell displays. */
const dateParts = (v) => {
  if (v instanceof Date) return { d: v.getDate(), m: v.getMonth() + 1, y: v.getFullYear() };
  if (typeof v === "number" && Number.isFinite(v)) {
    const p = XLSX.SSF?.parse_date_code(v);
    if (p && p.m && p.y) return { d: p.d, m: p.m, y: p.y };
  }
  return null;
};

const yy = (year) => String(year).slice(-2);
const pad2 = (n) => String(n).padStart(2, "0");

/* Expiry Date arrives as MM/YYYY ("01/2028") or MM/YY; normalise to MM/YY. */
export const curebayExpiry = (v) => {
  const parts = dateParts(v);
  if (parts) return `${pad2(parts.m)}/${yy(parts.y)}`;
  const s = String(v ?? "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{1,2})\s*[/\-.]\s*(\d{2}|\d{4})$/);
  if (!m) return s;
  return `${pad2(Number(m[1]))}/${yy(m[2].length === 4 ? m[2] : `20${m[2]}`)}`;
};

/* A usable expiry is MM/YY with a real month. Guards the skip rule below. */
const isMmYy = (s) => /^(0[1-9]|1[0-2])\/\d{2}$/.test(String(s || ""));

/* Invoice Date arrives as DD/MM/YY ("16/07/26") — day first, not month first —
   or DD/MM/YYYY. Returns the yyyy-mm-dd the header's date input needs. */
export const curebayInvoiceDate = (v) => {
  const parts = dateParts(v);
  if (parts) return `${parts.y}-${pad2(parts.m)}-${pad2(parts.d)}`;
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\s*[/\-.]\s*(\d{1,2})\s*[/\-.]\s*(\d{2}|\d{4})$/);
  if (!m) return "";
  const year = m[3].length === 4 ? m[3] : `20${m[3]}`;
  return `${year}-${pad2(Number(m[2]))}-${pad2(Number(m[1]))}`;
};

/* Units per strip from the Packaging label.
   "1X15" / "15'S" / "10S" -> the strip count.
   "60 ML" / "100ML" / "50GM" -> 1: a bottle or tube is a single sellable unit,
   so treating the volume as a unit count would badly skew per-unit pricing. */
export const curebayUnitPack = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (/^\d+(\.\d+)?\s*(ML|L|GM|GMS|G|KG|MG|MCG)\.?$/i.test(s)) return "1";
  const runs = s.match(/\d+/g);
  if (!runs) return "";
  const n = Number(runs[runs.length - 1]);
  return n >= 1 ? String(Math.round(n)) : "";
};

/* Parse Curebay-format sheet rows into GRN drawer lines + the invoice header
   fields the file carries.

   Incomplete rows are skipped rather than imported half-filled: a line missing
   any of these cannot be posted, and letting it through only surfaces later as a
   blocked GRN or a backend rejection.
     Batch Number
     Quantity    — must be greater than zero
     Expiry Date — must resolve to a real MM/YY
     PTR         — must be greater than zero
   `skippedReasons` counts each cause so the UI can say what was dropped instead
   of silently importing fewer rows than the file contained.

   A missing CBDrugCode is NOT a skip: the row imports with an empty product code
   and is counted in `unmapped`, so the operator can map it from the line table
   rather than losing an otherwise complete invoice line. */
export const parseCurebayRows = (rows) => {
  const lines = [];
  const skippedReasons = {};
  let skipped = 0;
  let unmapped = 0;
  let invoiceNumber = "";
  let invoiceDate = "";

  const skip = (reason) => {
    skipped++;
    skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
  };

  (rows || []).forEach((r) => {
    /* Invoice identity repeats on every row; take the first non-empty. */
    if (!invoiceNumber) invoiceNumber = String(col(r, "Invoice Number") || "").trim();
    if (!invoiceDate) invoiceDate = curebayInvoiceDate(col(r, "Invoice Date"));

    /* A wholly blank row (trailing newline, spacer row) is not a failure — drop
       it without counting it against the operator. */
    const anyValue = Object.values(r || {}).some((v) => String(v ?? "").trim() !== "");
    if (!anyValue) return;

    /* CBDrugCode is the mapping key when the export carries it. Current exports
       omit the column entirely, and their "Medicine Id" holds the same CureBay
       product id — verified against a custom-format file for the same invoice,
       whose Product Code column is an exact match for these Medicine Ids. So
       fall back to it rather than dropping a whole invoice.
       If neither is present the row still imports, with an empty product code
       counted in `unmapped`, and the operator maps it from the line table. */
    const productCode =
      String(col(r, "CBDrugCode", "CB Drug Code", "CBDrugcode") || "").trim() ||
      String(col(r, "Medicine Id", "MedicineId", "Medicine ID") || "").trim();
    if (!productCode) unmapped++;

    const batch = String(col(r, "Batch Number", "Batch") || "").trim();
    const quantity = numOf(col(r, "Quantity", "Qty"));
    const expiry = curebayExpiry(col(r, "Expiry Date", "Expiry"));
    const ptr = numOf(col(r, "PTR", "Purchase Price"));

    if (!batch) return skip("no batch");
    if (!(quantity > 0)) return skip("no quantity");
    if (!isMmYy(expiry)) return skip("bad expiry");
    if (!(ptr > 0)) return skip("no PTR");

    const taxPct = numOf(col(r, "Tax", "GST %"));
    const packaging = String(col(r, "Packaging", "Packing") || "").trim();

    /* Only the raw inputs are imported — "Product Discount" is a percentage and
       "Scheme Discount" a flat amount, and the existing pipeline (backend
       v1/calculations/purchase, with lineCalc as the transitional fallback)
       derives every money figure from them exactly as it does for the custom
       format. `discountAmount` is deliberately left blank so the line takes the
       PCT branch rather than a value computed here.
       The file's own TaxableValue / NetValue columns are therefore read-only
       cross-checks, not imported money.
       The scheme amount is rounded to paise on the way in: the export carries
       float artefacts ("184.2999963760376") that would otherwise be shown to the
       operator verbatim. */
    const discountPct = numOf(col(r, "Product Discount"));
    const schemeDiscount = round2(numOf(col(r, "Scheme Discount", "Scheme Disc")));

    lines.push({
      /* CBDrugCode is the mapping key — the CureBay drug code the GRN posts
         against. "Product Code" is the supplier's own SKU and is not used. */
      medId: productCode,
      productCode,
      productName: String(col(r, "Product Name", "Item Details") || "").trim(),
      manufacturer: String(col(r, "Manufacturer Name", "Manufacturer") || "").trim(),
      batch,
      quantity,
      free: "",
      expiry,
      discountPct,
      discountAmount: "",
      /* Flat ₹ off the gross, deducted before discountPct (they cascade). */
      schemeDiscount,
      mrp: numOf(col(r, "MRP")),
      purchasePrice: ptr,
      taxCategory: String(col(r, "HSN Code", "HSN") || "").trim(),
      purchaseTax: taxPct ? `GST ${taxPct}%` : "",
      taxPct,
      packing: packaging,
      unit: curebayUnitPack(packaging),
      /* The Curebay export carries no storage location — the rack is prefilled
         from the medicine's home shelf once the lines land, or picked by hand. */
      rackId: null,
      rackCode: "",
    });
  });

  return { lines, skipped, skippedReasons, unmapped, invoiceNumber, invoiceDate };
};

/* Header row + one sample line, for the downloadable Curebay template. */
export const CUREBAY_SAMPLE_ROW = [
  "26CBLR12640584",
  "16/07/26",
  "I17050",
  "BONTRESS PRO PLUS SCALP SERUM 60ML",
  "60 ML",
  "PCM0042A",
  "01/2028",
  1,
  0.0,
  4.0,
  9.48,
  12.44,
  "GLENMARK PHARMACEUTICALS LTD",
  5.0,
  9.1,
  "30049011",
  "11477260",
  9.56,
  3.0,
  1.0,
  "CB84561",
];
